<template>
  <div class="developer-personality">
    <h3 class="developer-personality__title">Soft skills model</h3>

    <v-row v-if="skills.agreeableness">
      <v-col cols="5">
        <ChartRadar :chart-data="datacollection" />
      </v-col>

      <v-col cols="7">
        <div class="developer-personality__details">
          <div class="developer-personality__statistics">
            <div
              v-for="(value, key, index) in skills"
              :key="index"
              class="developer-personality__statistic-element">
              <p class="caption mb-2">{{ index + 1 }}. {{ key }}</p>
              <p v-if="value" class="blue--text">
                {{ Math.ceil(value/7*100) }}%
              </p>
            </div>
          </div>

          <div v-if="!readonly" class="developer-personality__action">
            <v-btn class="fw-400"
              :loading="isDataLoading"
              medium
              color="blue"
              dark
              @click="showAnswers"
            >
              FORM PREVIEW
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-row v-else>
      <v-col>
        <div class="action-box">
          <v-btn
            class="action-box__btn"
            tile
            fab
            dark
            small
            elevation="0"
            color="blue-light"
            @click.stop="sendLink"
          >
            <v-icon color="blue" class="action-box__icon" small>mdi-send</v-icon>
          </v-btn>
          <div class="action-box__content">
            <h4 class="action-box__title">
              Developer hasn't filled the survey yet
            </h4>
            <p class="action-box__description">
              You won't see the chart until the developer completes the soft-skills survey
            </p>
          </div>
        </div>

        <template v-if="!readonly">
          <div v-if="isTokenPresent" class="developer-personality__link">
            <v-text-field
              :value="inviteLink"
              id="refLink"
              class="form-field"
              dense
              readonly
              label="Developer soft-skill analyze"
              placeholder="Link"
            />
            <v-btn
              class="developer-personality__link-btn"
              outlined
              fab
              tile
              color="blue"
              @click="copy"
            >
              <v-icon dark>mdi-link-variant</v-icon>
            </v-btn>
          </div>

          <div v-else class="mt-4 text-right">
            <v-btn class="fw-400"
              :loading="isProfileSubmitProcessing"
              medium
              color="blue"
              dark
              @click="getInvitationLink"
            >
              GET SURVEY LINK
            </v-btn>
          </div>
        </template>
      </v-col>
    </v-row>

    <SurveyAnswersModal v-model="isAnswersModalVisible" :answers="surveyAnswers" />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { copyToClipboard } from '@/helpers/utils';
import ChartRadar from '@/components/Developers/Show/ChartRadar.vue';
import SurveyAnswersModal from '@/components/Developers/Show/SurveyAnswersModal.vue';

const pageUrl = process.env.VUE_APP_FE_BASE_URL;

export default {
  components: {
    ChartRadar,
    SurveyAnswersModal,
  },

  props: {
    skills: {
      type: Object,
      required: true,
    },
    token: {
      type: String,
      required: false,
      default: '',
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      isAnswersModalVisible: false,
      surveyAnswers: [],
    };
  },

  computed: {
    ...mapState('hrDeveloper', [
      'isDataLoading',
      'isProfileSubmitProcessing',
    ]),

    datacollection() {
      const chartData = {
        labels: [
          '1',
          '2',
          '3',
          '4',
          '5',
        ],
        datasets: [
          {
            label: 'Score',
            backgroundColor: 'rgba(108, 123, 230, 0.5)',
            fill: true,
            data: this.skills ? Object.values(this.skills) : [],
          },
        ],
      };

      return chartData;
    },

    inviteLink() {
      return this.token ? `${pageUrl}/join/${this.token}` : '';
    },

    isTokenPresent() {
      return this.token && this.token.length > 0;
    },
  },

  methods: {
    ...mapActions('hrDeveloper', [
      'fetchDeveloperSurveyAnswers',
    ]),

    showAnswers() {
      this.fetchDeveloperSurveyAnswers(this.$route.params.id)
        .then((resp) => {
          this.surveyAnswers = resp.data;
          this.isAnswersModalVisible = true;
        });
    },

    sendLink() {},

    copy() {
      copyToClipboard(
        'refLink',
        this.copySucces,
        this.copyError,
      );
    },

    copySucces() {
      this.$store.dispatch('showSuccess', ['Link was copied ']);
    },

    copyError() {
      this.$store.dispatch('showError', ['Oops, unable to copy']);
    },

    getInvitationLink() {
      this.$emit('invitate');
    },
  },
};
</script>

<style lang="scss" scoped>
.developer-personality {
  margin-bottom: 24px;
  padding: 16px 24px;
  background: #FFFFFF;
  box-shadow: 0px 3px 6px #00000029;
  border-radius: 8px;

  &__title {
    color: $main--headers;
    font-size: 18px;
    line-height: 21px;
    font-weight: 500;
    margin-bottom: 8px;
  }

  &__details {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
  }

  &__action {
    text-align: right;
  }

  &__statistics {
    display: flex;
    flex-wrap: wrap;
  }

  &__statistic-element {
    flex: 0 0 50%;
    max-width: 50%;
    line-height: 16px;

    p {
      font-weight: 500;
      font-size: 14px;
      text-transform: capitalize;
    }
  }

  &__chart {
    max-width: 100%;
  }

  &__link {
    display: flex;
    margin-top: 16px;
  }
  &__link-btn {
    border-radius: 4px;
    border-color: $color--secondary;
    height: 54px;
    width: 54px;
    margin-left: 16px;
  }
}

.action-box {
  padding: 16px;
  display: flex;
  align-items: center;
  border: 1px dashed #E2E6EA;
  border-radius: 4px;

  &__btn {
    border-radius: 8px;
  }

  &__icon {
    transform: rotate(-45deg);
  }

  &__content {
    margin-left: 24px;
  }

  &__title {
    font-size: 14px;
    line-height: 16px;
    letter-spacing: 0;
    color: #394657;
    margin-bottom: 4px;
  }

  &__description {
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0;
    margin-bottom: 0;
    max-width: 540px;
  }
}
</style>
